<script setup>
import { useTeletelFeatures } from 'dashboard/composables/useTeletelFeatures';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import AssistantAPI from 'dashboard/api/teletel/assistant';

// Who answers in this conversation. One chip in the reply box, two states:
// «المساعد يرد» (press to take over) and «الموظف يرد» (press to hand it
// back to the assistant). Hidden when the inbox has no assistant. The state
// is refreshed when the conversation changes and after every message the
// agent sends, since an agent's reply silences the assistant on its own.
const props = defineProps({
  conversationId: { type: Number, default: null },
});

const { isTeletelFeatureEnabled } = useTeletelFeatures();

const { t } = useI18n();
const enabled = computed(() => isTeletelFeatureEnabled('assistant'));
const currentChat = useMapGetter('getSelectedChat');

const state = ref(null);
const busy = ref(false);

const linked = computed(() => Boolean(state.value?.linked));
const assistantMode = computed(() => state.value?.mode === 'assistant');

const load = async () => {
  if (!enabled.value || !props.conversationId) {
    state.value = null;
    return;
  }
  try {
    const { data } = await AssistantAPI.conversationState(props.conversationId);
    state.value = data.payload;
  } catch (e) {
    state.value = null;
  }
};

const toggle = async () => {
  if (busy.value || !props.conversationId) return;
  busy.value = true;
  try {
    const { data } = assistantMode.value
      ? await AssistantAPI.pauseAssistant(props.conversationId)
      : await AssistantAPI.resumeAssistant(props.conversationId);
    state.value = data.payload;
    useAlert(
      assistantMode.value
        ? t('TELETEL_ASSISTANT_TOGGLE.RESUMED')
        : t('TELETEL_ASSISTANT_TOGGLE.PAUSED')
    );
  } catch (e) {
    useAlert(e?.response?.data?.error || t('TELETEL_ASSISTANT_TOGGLE.ERROR'));
  } finally {
    busy.value = false;
  }
};

// The conversation object changes when a message lands (its last activity
// moves), so watching it keeps the chip honest after the agent replies.
watch(
  () => [
    props.conversationId,
    currentChat.value?.last_activity_at,
    enabled.value,
  ],
  load,
  {
    immediate: true,
  }
);
</script>

<template>
  <button
    v-if="linked"
    type="button"
    class="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium transition-colors"
    :class="
      assistantMode
        ? 'border-n-teal-7 bg-n-teal-3 text-n-teal-11 hover:bg-n-teal-4'
        : 'border-n-weak bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'
    "
    :disabled="busy"
    :title="
      assistantMode
        ? t('TELETEL_ASSISTANT_TOGGLE.PAUSE_HINT')
        : t('TELETEL_ASSISTANT_TOGGLE.RESUME_HINT')
    "
    @click="toggle"
  >
    <span
      :class="assistantMode ? 'i-lucide-bot' : 'i-lucide-user-round'"
      class="size-3.5"
    />
    {{
      assistantMode
        ? t('TELETEL_ASSISTANT_TOGGLE.ASSISTANT_ANSWERS')
        : t('TELETEL_ASSISTANT_TOGGLE.HUMAN_ANSWERS')
    }}
    <span class="text-[10px] opacity-70">
      {{
        assistantMode
          ? t('TELETEL_ASSISTANT_TOGGLE.PAUSE')
          : t('TELETEL_ASSISTANT_TOGGLE.RESUME')
      }}
    </span>
  </button>
</template>
